import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Chip } from '@material-ui/core';
import LoginIcon from '@material-ui/icons/ExitToApp';
import LogoutIcon from '@material-ui/icons/MeetingRoom';
import { useGestionMarcacionApi } from '../hooks/useGestionMarcacionApi';
import { toISODate } from '../utils/marcacionUtils';

interface Props {
  legajo: number | string;
  fecha: string;
  idMarcacionActual?: number;
}

interface MarcacionDia {
  operacion: string;
  idMarcacion: number;
  fecha: string;
  hora: string;
  nombre: string;
  legajo: string | number;
  cedula: string;
  estado: string | null;
  puntoServicio: string;
}

const colorEstado = (estado: string | null) => {
  const e = String(estado || '').toUpperCase().trim();
  if (e === 'SIN-ENTRADA' || e === 'SIN-SALIDA') return { bg: '#fff4e5', fg: '#b76e00', border: '#f3c98b' };
  if (e === 'SUPERA' || e.startsWith('GESTION-SUPERA')) return { bg: '#e8f4fd', fg: '#0d5fa6', border: '#a5c8e9' };
  if (e === 'REPETIDA') return { bg: '#fdecea', fg: '#b71c1c', border: '#f1a8a3' };
  if (!e) return { bg: '#e8f5e9', fg: '#2e7d32', border: '#a5d6a7' }; // sin estado = OK / procesada
  return { bg: '#f1f1f1', fg: '#555', border: '#ddd' };
};

const MarcacionesDiaPanel: React.FC<Props> = ({ legajo, fecha, idMarcacionActual }) => {
  const api = useGestionMarcacionApi();
  const [marcaciones, setMarcaciones] = useState<MarcacionDia[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let activo = true;
    const fechaISO = toISODate(fecha);
    if (!fechaISO || !legajo) return;
    setLoading(true);
    setError('');
    api.traerMarcacionesDia(legajo, fechaISO).then((res) => {
      if (!activo) return;
      if (res.ok && res.data) {
        const ordenadas = [...res.data].sort((a, b) => String(a.hora).localeCompare(String(b.hora)));
        setMarcaciones(ordenadas);
      } else {
        setError(res.error || 'No se pudieron obtener las marcaciones del día.');
      }
      setLoading(false);
    });
    return () => { activo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legajo, fecha]);

  return (
    <Box marginTop={2} marginBottom={1} padding={1.5} style={{ background: '#fafafa', border: '1px solid #ececec', borderRadius: 6 }}>
      <Typography variant="subtitle2" style={{ color: '#900533', marginBottom: 8 }}>
        Marcaciones del día ({marcaciones.length})
      </Typography>

      {loading && (
        <Box display="flex" alignItems="center" gridGap={8}>
          <CircularProgress size={16} style={{ color: '#900533' }} />
          <Typography variant="caption">Cargando marcaciones...</Typography>
        </Box>
      )}

      {error && (
        <Typography variant="caption" color="error">{error}</Typography>
      )}

      {!loading && !error && marcaciones.length === 0 && (
        <Typography variant="caption" color="textSecondary">Sin otras marcaciones registradas para este día.</Typography>
      )}

      {!loading && !error && marcaciones.length > 0 && (
        <Box display="flex" flexDirection="column" gridGap={6}>
          {marcaciones.map((m) => {
            const esActual = idMarcacionActual !== undefined && m.idMarcacion === idMarcacionActual;
            const c = colorEstado(m.estado);
            const esEntrada = String(m.operacion || '').toUpperCase() === 'HE';
            const tipoLabel = esEntrada ? 'ENTRADA' : 'SALIDA';
            return (
              <Box
                key={m.idMarcacion}
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gridGap={8}
                padding={1}
                style={{
                  background: esActual ? '#fff3e0' : '#ffffff',
                  border: `1px solid ${esActual ? '#ff9800' : '#e0e0e0'}`,
                  borderRadius: 4,
                  fontSize: 12,
                }}
              >
                {esEntrada
                  ? <LoginIcon style={{ fontSize: 16, color: '#2e7d32' }} />
                  : <LogoutIcon style={{ fontSize: 16, color: '#900533' }} />}
                <span style={{ fontWeight: 600, minWidth: 56 }}>{tipoLabel}</span>
                <span style={{ minWidth: 50 }}>{m.hora}</span>
                <span style={{ flex: 1, color: '#555' }}>{m.puntoServicio}</span>
                <Chip
                  size="small"
                  label={m.estado || 'PROCESADA'}
                  style={{
                    backgroundColor: c.bg,
                    color: c.fg,
                    border: `1px solid ${c.border}`,
                    fontSize: 10,
                    height: 20,
                    fontWeight: 600,
                  }}
                />
                {esActual && (
                  <Chip
                    size="small"
                    label="ACTUAL"
                    style={{ backgroundColor: '#ff9800', color: 'white', fontSize: 10, height: 20, fontWeight: 600 }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default MarcacionesDiaPanel;
